import { Button, FormField, Label, Select } from '@round/ui-kit';
import styles from './MicrosPaymentRequestForm.module.css';
import { Formik, Form, Field, FieldProps, ErrorMessage, FormikHelpers } from 'formik';
import cn from 'classnames';
import { microwave } from '@round/api';
import * as Yup from 'yup';
import { ErrorIcon } from './ErrorIcon/ErrorIcon';
import { ReactComponent as PayPalIcon } from 'assets/PayPal.svg';
import { ReactComponent as VenmoIcon } from 'assets/Venmo.svg';
import InputWithIcons from 'ui/DataEntry/InputWithIcons/InputWithIcons';
import RenderPostUrlIcon from './RenderPostUrlIcon/RenderPostUrlIcon';
import { StylesConfig } from 'react-select';
import { mapApiErrorsToFormikErrors } from 'utility/formik';
import { showNotification } from 'utility/showNotification';
import { useInfluencerLocationSelect } from 'hooks/useInfluencerLocationSelect';
import { HierarchicalOptionLabel } from '@round/ui-kit';
import { getPlatformLabel } from 'helpers';
import { FormData } from '../useMicrosPaymentRequest';
import { useValidatePostUrl } from './useValidatePostUrl';
import { sanitizeInstagramPostUrl } from '../../../utility/instagramPostValidation';

type FormValues = Omit<
    microwave.PostTiktokPostInviteFormApiBody,
    'location' | 'location_id' | 'preferred_payment_method' | 'payment_details'
> & {
    country_location_id: number | null;
    city_location_id: number | null;
    preferred_payment_method: Extract<microwave.PaymentMethod, 'VENMO' | 'PAYPAL'> | null;
};

type Props = {
    uuid: string;
    hasLocation: boolean | undefined;
    onSuccess: (data: Partial<FormData>) => void;
    platform: 'tiktok' | 'instagram';
};

const selectStyles: StylesConfig = {
    container: (base) => ({
        ...base,
        margin: 0,
    }),
    control: (base) => ({
        ...base,
        border: 'solid 1px #D2D2D2',
        borderRadius: '2rem',
        boxShadow: 'none',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
        fontSize: '0.8125rem',
    }),
};

const MicrosPaymentRequestForm = ({ uuid, onSuccess, hasLocation, platform }: Props) => {
    const platformLabel = getPlatformLabel(platform);

    const { selectProps: countrySelectProps } = useInfluencerLocationSelect({
        type: 'country',
        ordering: 'country_name',
    });
    const { selectProps: citySelectProps, reset: resetCitySelect } = useInfluencerLocationSelect({
        type: 'city',
        ordering: 'city',
        country_code: countrySelectProps.value?.countryCode,
    });

    const { postUrlInputStatus, postUrlValidationMessage, handleValidatePostUrl, resetPostUrlValidationState } =
        useValidatePostUrl(uuid, platform);

    const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        try {
            if (!values.preferred_payment_method) {
                helpers.setFieldError('preferred_payment_method', 'At least one payment method is required');
                return;
            }

            const submit =
                platform === 'tiktok' ? microwave.postTiktokPostInviteFormSubmission : microwave.postInviteForm;

            helpers.setSubmitting(true);
            const response = await submit(uuid, {
                post_url: platform === 'tiktok' ? values.post_url : sanitizeInstagramPostUrl(values.post_url),
                location_id: values.city_location_id ?? values.country_location_id ?? undefined,
                preferred_payment_method: values.preferred_payment_method,
                paypal_email_address: values.paypal_email_address,
                venmo_email_address: values.venmo_email_address,
            });

            if (response.status === 201) {
                onSuccess({ ...response.data, submission_date: new Date().toISOString().split('T')[0] });
                return;
            }

            if (Array.isArray(response.data)) {
                showNotification(response.data.join('. '), 'error');
                return;
            }

            helpers.setErrors(mapApiErrorsToFormikErrors<FormValues>(response.data));
        } catch {
            showNotification(`Couldn't submit the form. Please try again later.`, 'error');
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const validationSchema = Yup.object().shape(
        {
            post_url: Yup.string().required(`${platformLabel} Post URL is required`),
            preferred_payment_method: Yup.string().required('At least one payment method is required'),
            paypal_email_address: Yup.string()
                .when('preferred_payment_method', {
                    is: (value: microwave.PaymentMethod) => value === 'PAYPAL',
                    then: Yup.string().required('Payment details are required'),
                    otherwise: Yup.string(),
                })
                .email('Must be a valid email address'),
            venmo_email_address: Yup.string()
                .email('Must be a valid email address')
                .when('preferred_payment_method', {
                    is: (value: microwave.PaymentMethod) => value === 'VENMO',
                    then: Yup.string().required('Payment details are required'),
                    otherwise: Yup.string(),
                }),
        },
        [['paypal_email_address', 'venmo_email_address']]
    );

    const initialValues: FormValues = {
        post_url: '',
        country_location_id: null,
        city_location_id: null,
        preferred_payment_method: null,
        paypal_email_address: '',
        venmo_email_address: '',
    };

    const handleScrollIntoView = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (!(e.currentTarget instanceof HTMLButtonElement)) {
            return;
        }
        e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={handleSubmit}
        >
            {(props) => {
                const selectedPaymentMethod = props.values.preferred_payment_method;

                const handlePaymentMethodChange = (value: FormValues['preferred_payment_method']) => {
                    if (selectedPaymentMethod === value) {
                        return;
                    }

                    props.setErrors({
                        ...props.errors,
                        preferred_payment_method: '',
                        paypal_email_address: '',
                        venmo_email_address: '',
                    });
                    props.setValues({
                        ...props.values,
                        paypal_email_address: '',
                        venmo_email_address: '',
                    });

                    props.setFieldValue('preferred_payment_method', value);
                };

                return (
                    <Form className={styles.form}>
                        <FormField>
                            <Label htmlFor="post_url">Post URL</Label>
                            <Field name="post_url">
                                {(props: FieldProps<FormValues['post_url']>) => (
                                    <InputWithIcons
                                        name="post_url"
                                        className={cn(styles.field, styles.postUrl, {
                                            [styles.invalid]: postUrlInputStatus === 'INVALID',
                                        })}
                                        value={props.field.value}
                                        onChange={(e) => {
                                            resetPostUrlValidationState();
                                            props.form.setFieldValue('post_url', e.target.value);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleValidatePostUrl(props.field.value);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (e.target.value === '') {
                                                return;
                                            }
                                            handleValidatePostUrl(props.field.value);
                                        }}
                                        placeholder={`Enter ${platformLabel} Post link`}
                                        renderIconRight={() => (
                                            <RenderPostUrlIcon
                                                status={postUrlInputStatus}
                                                onSubmit={() => handleValidatePostUrl(props.field.value)}
                                            />
                                        )}
                                    />
                                )}
                            </Field>
                            {(!!postUrlValidationMessage || !!props.errors.post_url) && (
                                <div className={styles.errorContainer}>
                                    <ErrorIcon />
                                    <span>{postUrlValidationMessage || props.errors.post_url}</span>
                                </div>
                            )}
                        </FormField>

                        {postUrlInputStatus === 'VALID' && (
                            <>
                                <div className={styles.paymentOptionsContainer}>
                                    <Label htmlFor="preferred_payment_method">Please select payment method</Label>
                                    <article className={styles.scrollContainer}>
                                        <div className={styles.optionsContainer}>
                                            <Field name="preferred_payment_method">
                                                {() => (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                handlePaymentMethodChange('PAYPAL');
                                                                handleScrollIntoView(e);
                                                            }}
                                                            className={cn(styles.paymentButton, {
                                                                [styles.selected]: selectedPaymentMethod === 'PAYPAL',
                                                            })}
                                                        >
                                                            <PayPalIcon />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                handlePaymentMethodChange('VENMO');
                                                                handleScrollIntoView(e);
                                                            }}
                                                            className={cn(styles.paymentButton, {
                                                                [styles.selected]: selectedPaymentMethod === 'VENMO',
                                                            })}
                                                        >
                                                            <VenmoIcon />
                                                        </button>
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    </article>

                                    <ErrorMessage name="preferred_payment_method">
                                        {(msg) => (
                                            <div className={styles.errorContainer}>
                                                <ErrorIcon />
                                                <span>{msg}</span>
                                            </div>
                                        )}
                                    </ErrorMessage>
                                </div>

                                {!!selectedPaymentMethod && !hasLocation && platform !== 'instagram' && (
                                    <>
                                        <div className={styles.rowContainer}>
                                            <FormField>
                                                <Label htmlFor="country_location_id">Country</Label>
                                                <Field name="country_location_id" className={styles.field}>
                                                    {(props: FieldProps<FormValues['country_location_id']>) => (
                                                        <Select
                                                            name="country_location_id"
                                                            {...countrySelectProps}
                                                            onChange={(option) => {
                                                                props.form.setFieldValue(
                                                                    'country_location_id',
                                                                    option?.value
                                                                );

                                                                countrySelectProps.onChange(option);
                                                                citySelectProps.onChange(null);
                                                                resetCitySelect();
                                                            }}
                                                            styles={selectStyles}
                                                            placeholder="Select country"
                                                            isClearable
                                                            getOptionLabel={(option) => option.country}
                                                            captureMenuScroll={true}
                                                        />
                                                    )}
                                                </Field>
                                            </FormField>

                                            <FormField>
                                                <Label htmlFor="city_location_id">City</Label>
                                                <Field name="city_location_id" className={styles.field}>
                                                    {(props: FieldProps<FormValues['city_location_id']>) => (
                                                        <Select
                                                            name="city_location_id"
                                                            {...citySelectProps}
                                                            onChange={(option) => {
                                                                props.form.setFieldValue(
                                                                    'city_location_id',
                                                                    option?.value
                                                                );
                                                                citySelectProps.onChange(option);
                                                            }}
                                                            styles={selectStyles}
                                                            placeholder="Select city"
                                                            isClearable
                                                            isDisabled={!props.form.values.country_location_id}
                                                            getOptionLabel={(option) => option.city}
                                                            formatOptionLabel={(option, meta) => {
                                                                if (meta.context === 'menu') {
                                                                    return (
                                                                        <HierarchicalOptionLabel
                                                                            labels={[option.city, option.state]}
                                                                        />
                                                                    );
                                                                }
                                                                return option.city;
                                                            }}
                                                            captureMenuScroll={true}
                                                        />
                                                    )}
                                                </Field>
                                            </FormField>
                                        </div>
                                    </>
                                )}

                                {selectedPaymentMethod === 'PAYPAL' && (
                                    <div>
                                        <FormField>
                                            <Label htmlFor="paypal_email_address">PayPal Email Address</Label>
                                            <Field
                                                name="paypal_email_address"
                                                className={styles.field}
                                                placeholder="Your PayPal Email Address"
                                            />
                                            <ErrorMessage name="paypal_email_address">
                                                {(msg) => (
                                                    <div className={styles.errorContainer}>
                                                        <ErrorIcon />
                                                        <span>{msg}</span>
                                                    </div>
                                                )}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>
                                )}

                                {selectedPaymentMethod === 'VENMO' && (
                                    <div>
                                        <FormField>
                                            <Label htmlFor="venmo_email_address">Venmo Email Address</Label>
                                            <Field
                                                name="venmo_email_address"
                                                className={styles.field}
                                                placeholder="Your Venmo Email Address"
                                            />
                                            <ErrorMessage name="venmo_email_address">
                                                {(msg) => (
                                                    <div className={styles.errorContainer}>
                                                        <ErrorIcon />
                                                        <span>{msg}</span>
                                                    </div>
                                                )}
                                            </ErrorMessage>
                                        </FormField>
                                    </div>
                                )}

                                {!!selectedPaymentMethod && (
                                    <Button
                                        className={styles.submit}
                                        htmlType="submit"
                                        loading={props.isSubmitting}
                                        color="black"
                                        type="filled"
                                    >
                                        Submit Form
                                    </Button>
                                )}
                            </>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default MicrosPaymentRequestForm;
